import { deps_are_same_default } from './chunk-5D2Q75W2.mjs';
import { is_browser_default } from './chunk-DOLOJKHH.mjs';
import { use_unmount_default } from './chunk-EKEPE4ON.mjs';
import { isFunction, use_latest_default } from './chunk-24OWA2BQ.mjs';
import { useRef, useEffect } from 'react';

// src/utils/dom-target.ts
var getTargetElement = (target, defaultElement) => {
  if (!is_browser_default) {
    return;
  }
  if (!target) {
    return defaultElement;
  }
  let targetElement;
  if (isFunction(target)) {
    targetElement = target();
  } else if ("current" in target) {
    targetElement = target.current;
  } else {
    targetElement = target;
  }
  return targetElement;
};
var createEffectWithTarget = (useEffectType) => (
  /**
   * Builds an effect hook that re-runs the effect only when the target elements or the dependencies change.
   *
   * @param effect - The effect to run. The cleanup it returns runs before the next run and on unmount.
   * @param deps - Dependencies that trigger a re-run when they change.
   * @param target - target should compare ref.current vs ref.current, dom vs dom, ()=\>dom vs ()=\>dom
   */
  (effect, deps, target) => {
    const hasInitRef = useRef(false);
    const lastElementRef = useRef([]);
    const lastDepsRef = useRef([]);
    const unLoadRef = useRef(void 0);
    useEffectType(() => {
      const targets = Array.isArray(target) ? target : [target];
      const els = targets.map((item) => getTargetElement(item));
      if (!hasInitRef.current) {
        hasInitRef.current = true;
        lastElementRef.current = els;
        lastDepsRef.current = deps;
        unLoadRef.current = effect();
        return;
      }
      if (els.length !== lastElementRef.current.length || !deps_are_same_default(lastElementRef.current, els) || !deps_are_same_default(lastDepsRef.current, deps)) {
        unLoadRef.current?.();
        lastElementRef.current = els;
        lastDepsRef.current = deps;
        unLoadRef.current = effect();
      }
    });
    use_unmount_default(() => {
      unLoadRef.current?.();
      hasInitRef.current = false;
    });
  }
);
var create_effect_with_target_default = createEffectWithTarget;

// src/utils/use-effect-with-target.ts
var useEffectWithTarget = create_effect_with_target_default(useEffect);
var use_effect_with_target_default = useEffectWithTarget;

// src/dom/use-event-listener.ts
function useEventListener(eventName, handler, options = {}) {
  const { enable = true } = options;
  const handlerRef = use_latest_default(handler);
  use_effect_with_target_default(
    () => {
      if (!enable) {
        return;
      }
      const targetElement = getTargetElement(options.target, window);
      if (!targetElement?.addEventListener) {
        return;
      }
      const eventListener = (event) => {
        handlerRef.current(event);
      };
      const eventNameArray = Array.isArray(eventName) ? eventName : [eventName];
      for (const event of eventNameArray) {
        targetElement.addEventListener(event, eventListener, {
          capture: options.capture,
          once: options.once,
          passive: options.passive
        });
      }
      return () => {
        for (const event of eventNameArray) {
          targetElement.removeEventListener(event, eventListener, {
            capture: options.capture
          });
        }
      };
    },
    [eventName, options.capture, options.once, options.passive, enable],
    options.target
  );
}
var use_event_listener_default = useEventListener;

export { create_effect_with_target_default, getTargetElement, use_effect_with_target_default, use_event_listener_default };
